/**
 * Section 19 — Troubleshooting.
 *
 * Symptom → diagnosis → fix for the issues most likely to bite a new user.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

interface TBProps { id: string; symptom: string; cause: React.ReactNode; fix: React.ReactNode; }
const TB: React.FC<TBProps> = ({ id, symptom, cause, fix }) => (
  <Box id={`anchor-${id}`} sx={{ mb: 3, scrollMarginTop: 80, borderLeft: '3px solid', borderColor: 'divider', pl: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{symptom}</Typography>
    <P><strong>Cause:</strong> {cause}</P>
    <P><strong>Fix:</strong> {fix}</P>
  </Box>
);

const Section_troubleshooting: GuideSection = {
  id: 'troubleshooting',
  label: 'Troubleshooting',
  group: 'help',
  icon: <BugReportIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Troubleshooting</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Symptom → cause → fix. The issues users hit most often. Check <OpenInApp to="/logs" label="Logs" />
        first for any unexplained behaviour — every backend action is logged with a timestamp + reason.
      </Typography>

      <H id="broker">Broker connection issues</H>
      <TB
        id="ip-whitelist"
        symptom="ip_not_whitelisted_for_api_key error from Delta"
        cause={<>earngush's outbound IP (167.86.119.214) isn't in your Delta API key's allow-list. Delta rejects every API call without it.</>}
        fix={<>Go to Delta Exchange → Account → API → edit your key → add 167.86.119.214 to IP whitelist → save. Wait ~30 s for propagation, then click Test Connection on the broker row in earngush.</>}
      />
      <TB
        id="zerodha-token"
        symptom="Zerodha returns TokenException, all orders rejected"
        cause={<>Zerodha access tokens expire daily at ~06:00 IST. Every morning your first attempt fails until you re-login.</>}
        fix={<>Settings → Broker Credentials → click "Login with Zerodha" → complete OAuth + TOTP → bounce back. Test Connection should now show green.</>}
      />
      <TB
        id="bad-creds"
        symptom="Test Connection shows red error 'invalid api signature'"
        cause="Either API key or secret was mistyped, or the wrong field was pasted into the wrong slot."
        fix={<>Re-paste both fields carefully. Delta keys are 28 chars, secret 64 chars. Zerodha api_key is 16 chars, api_secret 32 chars. No leading/trailing whitespace.</>}
      />

      <H id="webhook">Webhook issues</H>
      <TB
        id="webhook-401"
        symptom="TradingView shows 401 when posting webhook"
        cause={<>X-Webhook-Secret header value doesn't match your per-user secret. Common causes: rotated the secret without updating TradingView, copy-pasted with whitespace, or used the old global secret.</>}
        fix={<>Settings → Webhooks → reveal current secret → copy. In TradingView alert dialog, update the custom-header value. Test via the Alerts page's Test Webhook button to confirm.</>}
      />
      <TB
        id="webhook-received-no-trade"
        symptom="Alert appears in Recent Alerts but no broker order is placed"
        cause={<>One of: (a) Auto Trade is OFF and you haven't clicked Execute; (b) Auto Trade is ON but the alert is CONFIRMED=N; (c) the daily-loss cap is breached (blocks entries only); (d) the Order Placement kill switch is OFF (blocks entries only); (e) the per-instrument Auto Long (BUY) / Auto Short (SHORT) flag is OFF → <em>AUTO SKIPPED · AL OFF / AS OFF</em> chip; (f) a position is already open for that symbol → <code>POSITION_OPEN</code> chip (no stacking); (g) placing the entry would breach the portfolio reserve → <code>RESERVE</code> chip. In cases (e)–(g) the alert is saved with an <code>autoSkipReason</code> and never reaches the broker.</>}
        fix={<>Dashboard → Trading Controls: both toggles ON. Verify the alert payload (expand the row) shows CONFIRMED=Y. Check the cap status in the RiskCockpit. If an AUTO SKIPPED chip is showing, read its reason: flip the per-symbol Auto Long / Auto Short toggle (AL/AS OFF), wait for the existing position to close (POSITION_OPEN), or free up margin / lower the reserve (RESERVE) — or click Execute to place the trade manually.</>}
      />
      <TB
        id="auto-skipped-chip"
        symptom="Alert shows orange AUTO SKIPPED · AL OFF or AS OFF chip"
        cause={<>R115 direction gate fired: Auto Trade is ON globally but the per-symbol Auto Long (for BUY) / Auto Short (for SHORT) flag on the Instruments page is OFF, so the order placement is gated. The alert is saved with <code>autoSkipReason</code> = AUTO_LONG_OFF or AUTO_SHORT_OFF.</>}
        fix={<>If you want auto-trading on that symbol's direction, flip the Auto Long / Auto Short toggle on the Instruments row. Otherwise click Execute on the alert to place the trade manually — the gate only blocks the auto-placement, not your manual override.</>}
      />
      <TB
        id="position-open-skip"
        symptom="Alert shows a POSITION_OPEN chip — a fresh entry signal fired but no order was placed"
        cause={<>The autonomous Auto-Trade path holds <strong>at most one position per symbol</strong> and does not scale in. A new BUY / SHORT signal arrived while an OPEN (or DRAFT) trade already exists for that symbol — from any scan timeframe — so the entry is skipped with <code>autoSkipReason = POSITION_OPEN</code>.</>}
        fix="No fix needed — it's by design (prevents double-firing across concurrent 1h / 4h scans). If you genuinely want to add to the position, do it manually from the Alerts / Charts UI; the auto path will resume placing fresh entries once the current position closes."
      />
      <TB
        id="validation-failed"
        symptom="API call returns 400 VALIDATION_FAILED with a fields map"
        cause={<>R115 Zod request-body validation rejected the payload. The <code>fields</code> map lists each invalid field and the reason — e.g. <code>{'{ email: "Invalid email", password: "Password must be at least 12 characters" }'}</code>.</>}
        fix="Correct the fields the response calls out and retry. The API never returns the request body back to you."
      />
      <TB
        id="idempotent-in-flight"
        symptom="POST /trades/* returns 409 IDEMPOTENT_IN_FLIGHT"
        cause={<>R115 idempotency-key middleware detected a still-running request with the same Idempotency-Key for your user. Common cause: a network blip and your client retried before the original returned.</>}
        fix={<>Wait a second or two and re-issue with the SAME Idempotency-Key — the middleware will return the cached response from the original request instead of placing another order. If you keep hitting 409, the original is hanging (broker latency) — check Trades to see if the order actually placed before re-trying with a NEW key.</>}
      />
      <TB
        id="account-deleted-login"
        symptom="Login returns ACCOUNT_DELETED instead of AUTH_INVALID"
        cause={<>PRE_LAUNCH M2 soft-delete window — an admin deleted your account and the 30-day grace period hasn't expired yet. Your data is still archived and can be restored.</>}
        fix={<>Contact the admin within the grace window to issue a POST /admin/users/:id/restore. After the window closes the data is hard-purged by the daily cron job and restore is no longer possible.</>}
      />
      <TB
        id="webhook-not-arriving"
        symptom="TradingView alert fires but no row appears in earngush"
        cause={<>Most likely: TradingView's webhook didn't reach earngush. Possible reasons: rate-limit hit (max 60 webhooks/minute), TradingView delivery delay (occasional), or wrong URL.</>}
        fix={<>In TradingView alert log, confirm the alert "fired" and shows a green response. If TradingView says "delivery failed", check your URL. Otherwise check earngush <OpenInApp to="/logs" label="Logs" /> for the inbound webhook around the alert timestamp.</>}
      />

      <H id="charts">Chart issues</H>
      <TB
        id="chart-blank"
        symptom="Chart loads but shows no candles"
        cause={<>Either: (a) the symbol's not tracked / no data has been polled, (b) broker credentials are missing for that symbol's exchange, (c) selected interval has no data for the symbol (e.g. 1m intraday on a delisted contract).</>}
        fix={<>Confirm the symbol's exchange is connected (Delta for crypto, Zerodha for NSE). For Zerodha symbols, ensure your access token is fresh. Try a different interval (1d usually has data even when intraday doesn't).</>}
      />
      <TB
        id="cmid-squashed"
        symptom="CMid line is wildly off the chart (way above price)"
        cause={<>Was an R71 bug — sliding-window null-drain in the sma() helper made CMid 10× too high when source had leading nulls. Fixed in R71.</>}
        fix="Should be impossible since R71. If you still see it, hard-refresh the chart (Ctrl-Shift-R) to bust the cache. If it persists, file an issue with the symbol + interval."
      />
      <TB
        id="of-panel-disagrees"
        symptom="Bottom-left OF panel says BULL but Flow ribbon's last bar is red"
        cause={<>Pre-R79 the two surfaces used different windows (rolling vs per-candle) so they could legitimately disagree. R79 aligned them.</>}
        fix={<>Should match now. If they still don't, hard-refresh the chart. If it persists, the bottom-left panel might be on its 30s button (rolling) while the ribbon is per-candle; switch the panel to 1m to align.</>}
      />

      <H id="orders">Order / trade issues</H>
      <TB
        id="order-rejected-margin"
        symptom="Order rejected — insufficient margin"
        cause={<>Either: (a) you actually don't have enough margin (check Available Margin tile), (b) the position size is too large for the symbol's lot rules.</>}
        fix={<>Lower the quantity in OrderConfirmDialog. For options, check the Trade Sizing strip at the bottom — it shows Required ₹ / Available ₹ as a coverage bar.</>}
      />
      <TB
        id="sl-too-close"
        symptom="SL or Target order rejected by the broker"
        cause={<>SL price is on the wrong side (e.g. SL above current price for a long) or too close to current (broker imposes a minimum tick distance).</>}
        fix={<>Move the SL further from current price. Click the SL cell on the Trades page, type a new value, press Enter. Watch for the green/red flash.</>}
      />
      <TB
        id="missing-positions"
        symptom="Trades page shows fewer open positions than I actually have at the broker"
        cause="Position sync hasn't caught up, or a position was opened outside earngush (manually at the broker)."
        fix="Manual broker positions aren't auto-imported. Either close them manually at the broker, or use Settings → Sync Positions to bring them into earngush (creates DRAFT trade rows you can then manage)."
      />

      <H id="auth">Auth issues</H>
      <TB
        id="locked-out"
        symptom="Login keeps failing even though I'm sure the password is right"
        cause={<>Rate limiter triggered. R80 #8 lowered auth rate-limit to 6 attempts per 15 min per (IP + email). After 6 failures you're locked out for the remainder of the window.</>}
        fix="Wait 15 minutes, then try again. Use the password manager next time. (We don't expose a self-unlock — would defeat the rate limit's purpose.)"
      />
      <TB
        id="password-rejected"
        symptom="Register or change-password rejects what feels like a strong password"
        cause={<>R80 #9 policy: minimum 12 characters, at least 3 of (lowercase / uppercase / digit / symbol), not in common-passwords blocklist, no whitespace.</>}
        fix="Add another character class or lengthen the password. Avoid 'password123' or '12345678' style — the blocklist explicitly rejects them."
      />
      <TB
        id="random-logout"
        symptom="Randomly bounced to the login screen / transient 'Network Error' on a few API calls"
        cause={<>A brief network blip or a single failed request used to be treated as an auth failure and cleared your session. This was hardened — transient network / API errors are now tolerated and no longer log you out.</>}
        fix={<>Should no longer happen on a flaky connection. If you <em>are</em> logged out, your token genuinely expired — just log back in. If a one-off "Network Error" toast appears, the app retries on the next poll; no action needed unless it persists (then check your connection or the <OpenInApp to="/logs" label="Logs" />).</>}
      />

      <H id="performance">Performance issues</H>
      <TB
        id="slow-trades-page"
        symptom="Trades page takes 5+ seconds to load"
        cause={<>Was an R69b bug — orders JOIN was happening in the list query making it O(n²). Fixed by dropping the JOIN + adding server-side pagination.</>}
        fix="Should be fast now (sub-1s for 50 rows). If it's still slow, check Logs for backend errors or try a more specific filter."
      />
      <TB
        id="dashboard-spinners"
        symptom="Dashboard cards show spinners forever"
        cause={<>Backend can't reach Postgres or Redis, or a downstream broker call is timing out. Check the Logs for connection errors.</>}
        fix={<>Refresh the page (Ctrl-Shift-R). If still hung, the Trading Controls panel's API call may have stalled — toggle the kill switch off-then-on to force a state refresh.</>}
      />

      <H id="when-stuck">When you're really stuck</H>
      <P>
        Check the <OpenInApp to="/logs" label="Logs" /> page first — every backend action with timestamps,
        levels, and full error messages. Filter by Level=error to skip the noise. Most weird behaviour
        either logs an explicit error or shows a missing log where one was expected.
      </P>
      <P>
        For UI weirdness: hard-refresh (Ctrl-Shift-R) often fixes stale-cache issues. PWA users may need
        to dismiss the update prompt from a recent deploy.
      </P>
    </>
  ),
  searchIndex: [
    { heading: 'Broker connection',          anchor: 'broker',                body: 'broker delta zerodha connection issues' },
    { heading: 'ip_not_whitelisted',         anchor: 'ip-whitelist',          body: 'ip whitelist delta allowlist' },
    { heading: 'Zerodha TokenException',     anchor: 'zerodha-token',         body: 'zerodha token exception expired' },
    { heading: 'invalid api signature',      anchor: 'bad-creds',             body: 'invalid api signature bad credentials' },
    { heading: 'Webhook issues',             anchor: 'webhook',               body: 'webhook issues 401 not arriving no trade' },
    { heading: 'Webhook 401',                anchor: 'webhook-401',           body: 'webhook 401 secret mismatch' },
    { heading: 'Alert without trade',        anchor: 'webhook-received-no-trade', body: 'alert no trade auto trade off confirmed cap kill switch auto long off auto short off' },
    { heading: 'AUTO SKIPPED chip',          anchor: 'auto-skipped-chip',     body: 'auto skipped al off as off auto long auto short instrument flag direction gate' },
    { heading: 'POSITION_OPEN skip',         anchor: 'position-open-skip',     body: 'position open skip no stacking one position per symbol autoSkipReason auto trade' },
    { heading: 'VALIDATION_FAILED',          anchor: 'validation-failed',     body: 'validation failed zod fields invalid email password' },
    { heading: 'IDEMPOTENT_IN_FLIGHT',       anchor: 'idempotent-in-flight',  body: 'idempotent in flight 409 idempotency key retry' },
    { heading: 'ACCOUNT_DELETED login',      anchor: 'account-deleted-login', body: 'account deleted login soft delete grace window restore' },
    { heading: 'Webhook not arriving',       anchor: 'webhook-not-arriving',  body: 'webhook not arriving rate limit delivery' },
    { heading: 'Chart issues',               anchor: 'charts',                body: 'chart issues blank cmid squashed of panel' },
    { heading: 'Chart blank',                anchor: 'chart-blank',           body: 'chart blank no candles symbol not tracked' },
    { heading: 'CMid squashed',              anchor: 'cmid-squashed',         body: 'cmid squashed wildly off line' },
    { heading: 'OF panel disagrees',         anchor: 'of-panel-disagrees',    body: 'of panel disagrees ribbon different bias' },
    { heading: 'Order / trade issues',       anchor: 'orders',                body: 'order trade issues rejected margin' },
    { heading: 'Insufficient margin',        anchor: 'order-rejected-margin', body: 'insufficient margin order rejected size lot' },
    { heading: 'SL too close',               anchor: 'sl-too-close',          body: 'sl too close rejected wrong side tick' },
    { heading: 'Missing positions',          anchor: 'missing-positions',     body: 'missing positions sync broker manual' },
    { heading: 'Auth issues',                anchor: 'auth',                  body: 'auth issues locked out password rejected' },
    { heading: 'Locked out',                 anchor: 'locked-out',            body: 'locked out rate limit auth attempts' },
    { heading: 'Password rejected',          anchor: 'password-rejected',     body: 'password rejected policy weak common blocklist' },
    { heading: 'Random logout / Network Error', anchor: 'random-logout',      body: 'random logout logged out network error transient session resilience token expired' },
    { heading: 'Performance',                anchor: 'performance',           body: 'performance slow trades dashboard spinner' },
    { heading: 'Slow Trades page',           anchor: 'slow-trades-page',      body: 'slow trades page join pagination' },
    { heading: 'Dashboard spinners',         anchor: 'dashboard-spinners',    body: 'dashboard spinners postgres redis broker timeout' },
  ],
};

export default Section_troubleshooting;
